import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { twMerge } from "tailwind-merge";
import { Loader2 } from "lucide-react";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer transition-all",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        brandPrimary:
          "bg-collectr-brand-primary text-background shadow-xs hover:bg-collectr-brand-primary/90 ring-collectr-brand-primary",
        brandSecondary:
          "bg-collectr-brand-secondary text-black shadow-xs hover:bg-collectr-brand-secondary/90 ring-collectr-brand-secondary",
        destructive: "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground border-border dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "border-border focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:ring aria-invalid:ring-destructive/20 dark:bg-input/30 dark:hover:bg-input/50 gap-2 rounded-md border",
        ghost:
          "dark:hover:bg-input/50 hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:ring",
        brandGhost:
          "dark:hover:bg-collectr-brand-primary/10 hover:bg-collectr-brand-primary/20 text-collectr-brand-primary focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:ring",
        link: "text-primary underline-offset-4 hover:underline",
        none: "bg-transparent text-primary hover:bg-transparent hover:text-primary/90 ring-input focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:ring"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        none: ""
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

interface ICustomButtonProps extends ButtonProps {
  isLoading?: boolean;
  loaderClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ICustomButtonProps>(
  ({ children, isLoading, className, loaderClassName,variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <>
        <Comp
          className={twMerge(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
          {isLoading && <Loader2 className={twMerge('ml-2 h-4 w-4 animate-spin', loaderClassName)} />}
        </Comp>
      </>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
